/**
 * TextMessage Component
 *
 * Renders text-based chat messages including plain text, buttons, and FAQ responses.
 * Supports product cards and reply message context.
 */

import React, { FC } from "react"
import clsx from "clsx"
import { convertTextStyle } from "src/util/TextFormatter"
import ChatMessageReplyView from "src/app/layout/chat-handle/components/chat-replymessage/ChatMessageReplyView"
import { TextMessageProps } from "../../types/MessageTypes"
import ProductVariantList from "./ProductVariantList"

const TextMessage: FC<TextMessageProps> = ({
  message,
  bgChat,
  txChat,
  isTabletOrMobile,
  replyMessageID,
  customer,
  phoneNumber,
  debug = false,
}) => {
  const maxWidthClass = isTabletOrMobile ? "mw-200px" : "mw-lg-300px"

  /**
   * Renders the product card if message has product data
   */
  const renderProductCard = () => {
    if (!message?.product?.displayName || message.product.displayName === "") {
      return null
    }

    return (
      <div
        className={clsx(
          "product-order-card rounded",
          bgChat,
          "bg-opacity-25"
        )}
        style={{ width: "fit-content" }}
      >
        <div className="d-flex flex-row align-items-start col-gap-2">
          {/* Product image */}
          {message?.product?.imageUrl && (
            <div className="product-image-container" style={{ color: txChat }}>
              <img
                src={message.product.imageUrl}
                alt="product"
                className="product-image-size"
              />
            </div>
          )}

          {/* Product info */}
          <div className="product-info-container">
            <div className="product-name">{message.product.displayName}</div>
            {message.product.sku && message.product.sku !== "" && !message.product.variants?.length && (
              <div className="product-sku">
                {`SKU: ${message.product.sku}`}
              </div>
            )}
            {message.product.displayPrice && message.product.displayPrice !== "" && !message.product.variants?.length && (
              <div className="product-price">
                {message.product.displayPrice.toString().match(/^[Rr]p/)
                  ? message.product.displayPrice
                  : `Rp. ${message.product.displayPrice.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".")}`}
              </div>
            )}
            {!message.product.variants?.length && message.product.stock !== undefined && message.product.stock !== null && (
              <div className={message.product.stock === 0 ? "product-stock-empty" : "product-stock"}>
                {message.product.stock === 0 ? "Habis" : `Stok: ${message.product.stock}`}
              </div>
            )}
          </div>
        </div>

        {/* Variant list — inside the card, below product info */}
        {message.product.variants && message.product.variants.length > 0 && (
          <ProductVariantList variants={message.product.variants} />
        )}
      </div>
    )
  }

  /**
   * Renders the text content with styling
   */
  const renderTextContent = () => {
    if (!message.textContent || message.textContent.trim() === "") {
      return null
    }

    // Skip text if it just duplicates the product display name
    if (message.product?.displayName && message.textContent.trim() === message.product.displayName.trim()) {
      return null
    }

    const displayText = debug
      ? JSON.stringify({
          messageId: message?.messageId,
          conversationId: message?.conversationId,
          collaborationId: message?.collaboration?.id,
          roomId: message?.room?.id,
        })
      : message.textContent.replaceAll("\n", "<br/>")

    return (
      <div
        className={clsx("chat-bubble-padding rounded fw-bold", bgChat, maxWidthClass)}
        style={{ color: txChat }}
        dangerouslySetInnerHTML={{
          __html: convertTextStyle(displayText),
        }}
      />
    )
  }

  /**
   * Renders text content with reply context
   */
  const renderWithReply = () => (
    <ChatMessageReplyView
      bgChat={bgChat}
      replyMessageID={replyMessageID}
      customer={customer}
      phoneNumber={phoneNumber}
    >
      <div
        style={{ color: txChat }}
        dangerouslySetInnerHTML={{
          __html: convertTextStyle(message.textContent.replaceAll("\n", "<br/>")),
        }}
        className={clsx("p-2 rounded fw-bold", bgChat, maxWidthClass)}
      />
    </ChatMessageReplyView>
  )

  /**
   * Renders standard text content with optional product card
   */
  const renderStandard = () => (
    <div className={clsx(`rounded d-flex flex-column ${bgChat}`, maxWidthClass)}>
      {renderProductCard()}
      {renderTextContent()}
    </div>
  )

  // Render with or without reply context
  if (message.replyMessageID) {
    return renderWithReply()
  }

  return renderStandard()
}

export default TextMessage
